import { useState } from 'react';
import { Button, Chip, IconButton, Stack, TextField, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material';
import { Add, Edit, ListAlt } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { EditableItemsTable } from '../../components/EditableItemsTable';
import { boqTemplatesApi, type BoqTemplate } from '../../api/boqMasters.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

type Row = Record<string, unknown>;
const ITEM_COLUMNS = [
  { key: 'description', label: 'Description' },
  { key: 'unit', label: 'Unit', width: 90 },
  { key: 'defaultQuantity', label: 'Qty', type: 'number' as const, width: 90 },
  { key: 'defaultClientRate', label: 'Client Rate', type: 'number' as const, width: 120 },
  { key: 'defaultExecutionRate', label: 'Exec Rate', type: 'number' as const, width: 120 },
  { key: 'taxPercent', label: 'Tax %', type: 'number' as const, width: 90 },
];

export function TemplateListPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [headerOpen, setHeaderOpen] = useState(false);
  const [editing, setEditing] = useState<BoqTemplate | null>(null);
  const [name, setName] = useState('');
  const [itemsFor, setItemsFor] = useState<BoqTemplate | null>(null);
  const [items, setItems] = useState<Row[]>([]);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['boq-templates'], queryFn: () => boqTemplatesApi.list({ limit: 100 }) });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['boq-templates'] });

  const saveHeader = useMutation({
    mutationFn: () => (editing ? boqTemplatesApi.update(editing.id, { name }) : boqTemplatesApi.create({ name })),
    onSuccess: () => { setHeaderOpen(false); setEditing(null); invalidate(); },
    onError: (e) => setError(extractApiError(e)),
  });
  const saveItems = useMutation({
    mutationFn: () => boqTemplatesApi.setItems(itemsFor!.id, items),
    onSuccess: () => setItemsFor(null),
    onError: (e) => setError(extractApiError(e)),
  });

  const openItems = async (t: BoqTemplate) => {
    setError(null);
    const detail = await boqTemplatesApi.getWithItems(t.id);
    setItems((detail.items as Row[]) ?? []);
    setItemsFor(t);
  };

  const columns: Column<BoqTemplate>[] = [
    { key: 'code', label: 'Code', width: 110 },
    { key: 'name', label: 'Name' },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" label={r.status} color={r.status === 'active' ? 'success' : 'default'} /> },
  ];

  return (
    <>
      <PageHeader title="BOQ Template Management" action={hasPermission('boq_template.create') && <Button startIcon={<Add />} variant="contained" onClick={() => { setEditing(null); setName(''); setError(null); setHeaderOpen(true); }}>New Template</Button>} />
      <DataTable
        columns={columns} rows={data?.data ?? []} loading={isLoading}
        page={1} limit={data?.data.length || 25} total={data?.meta.total ?? 0}
        onPageChange={() => undefined} onLimitChange={() => undefined} rowKey={(r) => r.id}
        actions={(r) => (
          <>
            <Tooltip title="Edit items"><IconButton size="small" onClick={() => openItems(r)}><ListAlt fontSize="small" /></IconButton></Tooltip>
            {hasPermission('boq_template.update') && <Tooltip title="Edit"><IconButton size="small" onClick={() => { setEditing(r); setName(r.name); setError(null); setHeaderOpen(true); }}><Edit fontSize="small" /></IconButton></Tooltip>}
          </>
        )}
      />

      <Dialog open={headerOpen} onClose={() => setHeaderOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{editing ? 'Edit Template' : 'New Template'}</DialogTitle>
        <DialogContent><Stack spacing={2} mt={1}>{error && <Alert severity="error">{error}</Alert>}<TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} required fullWidth /></Stack></DialogContent>
        <DialogActions><Button onClick={() => setHeaderOpen(false)}>Cancel</Button><Button variant="contained" onClick={() => saveHeader.mutate()} disabled={!name}>Save</Button></DialogActions>
      </Dialog>

      <Dialog open={Boolean(itemsFor)} onClose={() => setItemsFor(null)} fullWidth maxWidth="lg">
        <DialogTitle>Template Items — {itemsFor?.name}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
          <EditableItemsTable
            columns={ITEM_COLUMNS}
            rows={items}
            onChange={setItems}
            disabled={!hasPermission('boq_template.update')}
            newRow={() => ({ description: '', unit: '', defaultQuantity: 0, defaultClientRate: 0, defaultExecutionRate: 0, taxPercent: 0 })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setItemsFor(null)}>Close</Button>
          {hasPermission('boq_template.update') && <Button variant="contained" onClick={() => saveItems.mutate()}>Save Items</Button>}
        </DialogActions>
      </Dialog>
    </>
  );
}
